import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { useT } from "../i18n";

/**
 * Configuración de 2FA (TOTP): muestra el QR, verifica el primer código y
 * enseña los códigos de respaldo. Se usa tanto en el alta voluntaria (perfil,
 * token de sesión) como en el alta forzada por la organización (login, enroll
 * token). `token` es el Bearer a usar; `onDone` recibe la respuesta de
 * /2fa/enable (que incluye tokens de sesión).
 */
export default function TwoFactorSetup({ token, onDone, onCancel }: {
  token: string;
  onDone: (data: any) => void;
  onCancel?: () => void;
}) {
  const t = useT();
  const [secret, setSecret] = useState("");
  const [qr, setQr] = useState("");
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState<string[] | null>(null);
  const [pending, setPending] = useState<any>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/api/auth/2fa/setup", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(t("twoFactor.setupError"));
        const data = await res.json();
        setSecret(data.secret);
        setQr(await QRCode.toDataURL(data.otpauth_uri, { margin: 1, width: 200 }));
      } catch (e) {
        setError(e instanceof Error ? e.message : t("twoFactor.error"));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const verify = async () => {
    setVerifying(true);
    setError("");
    try {
      const res = await fetch("/api/auth/2fa/enable", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail ?? t("twoFactor.wrongCode"));
      }
      const data = await res.json();
      setBackup(data.backup_codes);
      setPending(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("twoFactor.error"));
    } finally {
      setVerifying(false);
    }
  };

  const box: React.CSSProperties = {
    padding: "12px 14px", borderRadius: 12, border: "1px solid var(--clr-border)",
    background: "var(--clr-surface2)", fontSize: "0.85rem",
  };

  if (loading) return <p style={{ color: "var(--clr-text3)" }}>{t("twoFactor.preparing")}</p>;

  // Paso final: mostrar códigos de respaldo (una sola vez)
  if (backup) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <h3 style={{ margin: "0 0 6px" }}>{t("twoFactor.activatedTitle")}</h3>
          <p style={{ fontSize: "0.85rem", color: "var(--clr-text2)", margin: 0 }}>
            {t("twoFactor.backupPre")}<strong>{t("twoFactor.backupTerm")}</strong>{t("twoFactor.backupPost")}
          </p>
        </div>
        <div style={{ ...box, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontFamily: "monospace", fontSize: "0.95rem", letterSpacing: 1 }}>
          {backup.map(c => <span key={c}>{c}</span>)}
        </div>
        <button onClick={() => onDone(pending)} style={{ padding: "10px 16px", borderRadius: 10, border: "none", background: "#0891b2", color: "#fff", fontWeight: 600, cursor: "pointer" }}>
          {t("twoFactor.backupSaved")}
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <p style={{ fontSize: "0.85rem", color: "var(--clr-text2)", margin: 0 }}>
        {t("twoFactor.scanInstructions")}
      </p>
      {qr && <img src={qr} alt={t("twoFactor.qrAlt")} style={{ alignSelf: "center", borderRadius: 12, border: "1px solid var(--clr-border)" }} />}
      <div style={{ ...box, fontFamily: "monospace", wordBreak: "break-all" }}>
        <span style={{ color: "var(--clr-text3)", fontSize: "0.72rem" }}>{t("twoFactor.manualKeyPrompt")}</span><br />
        {secret}
      </div>
      <input
        value={code}
        onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        placeholder={t("twoFactor.codePlaceholder")}
        inputMode="numeric"
        style={{ padding: "12px 14px", borderRadius: 10, border: "1px solid var(--clr-border)", fontSize: "1.1rem", letterSpacing: 4, textAlign: "center" }}
      />
      {error && <div style={{ color: "#ef4444", fontSize: "0.82rem" }}>{error}</div>}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        {onCancel && <button onClick={onCancel} style={{ padding: "10px 16px", borderRadius: 10, border: "1px solid var(--clr-border)", background: "transparent", cursor: "pointer" }}>{t("twoFactor.cancel")}</button>}
        <button onClick={verify} disabled={code.length !== 6 || verifying}
          style={{ padding: "10px 16px", borderRadius: 10, border: "none", background: "#0891b2", color: "#fff", fontWeight: 600, cursor: code.length === 6 ? "pointer" : "default", opacity: code.length === 6 ? 1 : 0.5 }}>
          {verifying ? t("twoFactor.verifying") : t("twoFactor.enable")}
        </button>
      </div>
    </div>
  );
}
